import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Compact “i” icon; tooltip in a portal (no overflow clip). Tap-friendly on mobile.
 */
export default function InfoHoverTip({ text, className = '', placement = 'top' }) {
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState({});
  const btnRef = useRef(null);
  const tipId = useId();

  const updatePosition = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const gap = 8;
    const maxW = Math.min(280, window.innerWidth - 24);
    const centerX = r.left + r.width / 2;
    const left = Math.min(Math.max(12, centerX), window.innerWidth - 12);
    const spaceBelow = window.innerHeight - r.bottom;
    const spaceAbove = r.top;
    const preferBottom = placement === 'bottom' || (placement === 'top' && spaceBelow >= spaceAbove);

    if (preferBottom && spaceBelow > 48) {
      setStyle({
        position: 'fixed',
        top: Math.min(r.bottom + gap, window.innerHeight - 12),
        left,
        transform: 'translateX(-50%)',
        maxWidth: maxW,
        zIndex: 10060,
      });
    } else {
      setStyle({
        position: 'fixed',
        top: Math.max(12, r.top - gap),
        left,
        transform: 'translate(-50%, -100%)',
        maxWidth: maxW,
        zIndex: 10060,
      });
    }
  }, [placement]);

  const showTip = useCallback(() => {
    updatePosition();
    setOpen(true);
  }, [updatePosition]);

  const hideTip = useCallback(() => setOpen(false), []);

  const toggleTip = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (open) hideTip();
      else showTip();
    },
    [open, hideTip, showTip]
  );

  useEffect(() => {
    if (!open) return undefined;
    const onReposition = () => updatePosition();
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    return () => {
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
  }, [open, updatePosition]);

  if (!text) return null;

  const tooltip =
    open &&
    createPortal(
      <span
        id={tipId}
        role="tooltip"
        style={style}
        className="pointer-events-none rounded-md border border-gray-200 bg-gray-900 px-2.5 py-2 text-left text-[11px] font-normal leading-snug text-white shadow-lg break-words"
      >
        {text}
      </span>,
      document.body
    );

  return (
    <>
      <span className={`inline-flex align-middle shrink-0 ${className}`}>
        <button
          ref={btnRef}
          type="button"
          tabIndex={0}
          className="inline-flex h-5 w-5 sm:h-4 sm:w-4 items-center justify-center rounded-full border border-gray-300 bg-white text-[10px] font-bold leading-none text-gray-500 hover:border-indigo-400 hover:text-indigo-600 focus:outline-none focus:ring-1 focus:ring-indigo-400 touch-manipulation"
          aria-label="More information"
          aria-describedby={open ? tipId : undefined}
          aria-expanded={open}
          onMouseEnter={showTip}
          onMouseLeave={hideTip}
          onFocus={showTip}
          onBlur={hideTip}
          onClick={toggleTip}
        >
          i
        </button>
      </span>
      {tooltip}
    </>
  );
}
